import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, FileText, MapPin } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import StatTile from "@/components/StatTile";
import { StatusPill, type StatusVariant } from "@/components/ui/StatusPill";
import { FIELD_METRICS, type ReadingStatus } from "@/lib/field-metrics";
import { SAMPLE_SITES, SAMPLE_READINGS, DEMO_DAYS } from "@/lib/sample-dashboard";
import { SAMPLE_REPORTS } from "@/lib/sample-reports";

const VARIANT: Record<ReadingStatus, StatusVariant> = { normal: "success", warning: "warning", alert: "danger" };
const LABEL: Record<ReadingStatus, string> = { normal: "Normal", warning: "Warning", alert: "Critical" };

function dayLabel(day: number) {
  const d = new Date();
  d.setDate(d.getDate() - (DEMO_DAYS - 1 - day));
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function worst(list: ReadingStatus[]): ReadingStatus {
  return list.includes("alert") ? "alert" : list.includes("warning") ? "warning" : "normal";
}

export default function PublicDemo() {
  const [siteId, setSiteId] = useState(SAMPLE_SITES[0].id);
  const site = SAMPLE_SITES.find((s) => s.id === siteId)!;

  const siteStatus = useMemo(() => Object.fromEntries(SAMPLE_SITES.map((s) => [
    s.id, worst(SAMPLE_READINGS.filter((r) => r.siteId === s.id && r.day === DEMO_DAYS - 1).map((r) => r.status)),
  ])), []);

  const alerts = useMemo(() => SAMPLE_READINGS.filter((r) => r.status !== "normal")
    .sort((a, b) => b.day - a.day), []);
  const last7 = SAMPLE_READINGS.filter((r) => r.day >= DEMO_DAYS - 7);
  const openWarnings = last7.filter((r) => r.status !== "normal").length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-16">
          <Link to="/" aria-label="Home"><Logo /></Link>
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline"><Link to="/auth">Sign in</Link></Button>
            <Button asChild size="sm"><Link to="/auth">Sign up</Link></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <div className="glass-card p-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm"><strong>Sample data</strong> — sign up to track your own sites.</p>
          <Button asChild size="sm"><Link to="/auth">Sign up free</Link></Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold">Live demo dashboard</h1>
          <p className="text-muted-foreground mt-1">Example sites, field readings, alerts and reports.</p>
        </div>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" aria-label="Summary">
          <StatTile label="Sites" value={SAMPLE_SITES.length} icon={<MapPin className="h-5 w-5" />} accent={1} />
          <StatTile label="Readings (7 days)" value={last7.length} icon={<Activity className="h-5 w-5" />} accent={2} />
          <StatTile label="Warnings (7 days)" value={openWarnings} icon={<AlertTriangle className="h-5 w-5" />} accent={3} />
          <StatTile label="Reports" value={SAMPLE_REPORTS.length} icon={<FileText className="h-5 w-5" />} accent={4} />
        </section>

        <section aria-labelledby="sites-h">
          <h2 id="sites-h" className="text-xl font-semibold mb-3">Sites</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SAMPLE_SITES.map((s) => (
              <button key={s.id} onClick={() => setSiteId(s.id)} aria-pressed={s.id === siteId}
                className={`glass-card press p-4 text-left ${s.id === siteId ? "ring-2 ring-primary" : ""}`}>
                <p className="font-medium">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.city}, {s.state}</p>
                <StatusPill className="mt-2" variant={VARIANT[siteStatus[s.id]]}>{LABEL[siteStatus[s.id]]}</StatusPill>
              </button>
            ))}
          </div>
        </section>

        <section aria-labelledby="readings-h">
          <h2 id="readings-h" className="text-xl font-semibold mb-3">Readings — {site.name}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FIELD_METRICS.map((m) => {
              const data = SAMPLE_READINGS.filter((r) => r.siteId === site.id && r.metric === m.key)
                .map((r) => ({ day: dayLabel(r.day), value: r.value }));
              const latest = data[data.length - 1]?.value;
              return (
                <div key={m.key} className="glass-card p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium">{m.label}</span>
                    <span className="text-muted-foreground">{latest} {m.unit}</span>
                  </div>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <ReferenceArea y1={m.warn[0]} y2={m.warn[1]} fill="hsl(var(--success))" fillOpacity={0.08} />
                        <XAxis dataKey="day" fontSize={10} stroke="hsl(var(--muted-foreground))" />
                        <YAxis fontSize={10} width={40} stroke="hsl(var(--muted-foreground))" />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section aria-labelledby="alerts-h">
          <h2 id="alerts-h" className="text-xl font-semibold mb-3">Recent alerts</h2>
          <ul className="glass-card divide-y divide-border">
            {alerts.slice(0, 10).map((a, i) => {
              const m = FIELD_METRICS.find((x) => x.key === a.metric)!;
              const s = SAMPLE_SITES.find((x) => x.id === a.siteId)!;
              return (
                <li key={i} className="flex items-center justify-between p-3 text-sm">
                  <span>{s.name} · {m.label} {a.value} {m.unit}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-muted-foreground">{dayLabel(a.day)}</span>
                    <StatusPill variant={VARIANT[a.status]}>{LABEL[a.status]}</StatusPill>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        <section aria-labelledby="reports-h">
          <div className="flex justify-between items-center mb-3">
            <h2 id="reports-h" className="text-xl font-semibold">Reports</h2>
            <Link to="/sample-reports" className="text-sm text-primary underline">View all sample reports</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {SAMPLE_REPORTS.slice(0, 4).map((r) => (
              <div key={r.id} className="glass-card p-4">
                <p className="font-medium">{r.title}</p>
                <p className="text-xs text-muted-foreground">{r.type} · {r.city}, {r.state}</p>
                <p className="text-sm mt-2">{r.summary}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
